import { useMemo, useState } from "react"
import { PageHeader } from "../../../../shared/components/pageHeader/PageHeader"
import { LocalizedStrict } from "../../../localization/components/AppLocalized"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faInfoCircle } from "@fortawesome/pro-light-svg-icons"
import { Page } from "../../../../shared/components/pageHeader/PageHeaderApi"
import { useAppLocalization } from "../../../localization/components/AppLocalizationProvider"
import { MenuItem } from "@mui/material"
import { useNavigate } from "react-router-dom"
import { StandardButton } from "../../../../shared/components/buttons/StandardButton"
import {
    getEditMerchantAdminRoute,
    MERCHANT_ADMIN_CREATE_ROUTE,
    MERCHANT_ADMIN_ROUTE,
} from "../../../../constants/routes"
import { useApiQueryTable } from "../../../../core/api/components/ApiQueryTable"
import { UserDetailsDialog } from "../../../office/admin/components/UserDetails/UserDetailsDialog"
import { selectEffectiveCompanyId } from "../../../context/contextSelectors"
import { useAppSelector } from "../../../store/storeHooks"
import { FetchMerchantAdminsResponse, MerchantQuery, fetchMerchantAdmins } from "../merchantAdminApi"
import { useApiQuery } from "../../../../core/api/useApiQuery"
import { MRT_ColumnDef } from "material-react-table"
import { useApiMutation } from "../../../../core/api/useApiMutation"
import { Admin } from "../../../users/userTypes"
import { deleteAdmin } from "../../../users/userApi"
import { enqueueSnackbar } from "notistack"
import { DeleteConfirmDialog } from "../../../../shared/components/dialogs/DeleteConfirmDialog"
import {
    Breadcrumb,
    BreadcrumbLink,
    StandardBreadcrumbs,
} from "../../../../shared/components/links/StandardBreadcrumbs"
import { DestructiveMenuItem } from "../../../../shared/components/menus/DestructiveMenuItem"
import {
    ImpersonationConfirmDialog,
    type ImpersonationTarget,
    toImpersonationTarget,
} from "../../../../shared/components/dialogs/ImpersonationConfirmDialog"
import { useCanImpersonate } from "../../../../shared/hooks/useCanImpersonate"

function transformData(data: FetchMerchantAdminsResponse) {
    return data.administrators
}

export function MerchantUsers() {
    const merchantId = useAppSelector(selectEffectiveCompanyId)
    const [showDeleteModal, setShowDeleteModal] = useState(false)
    const [showDetailsModal, setShowDetailsModal] = useState(false)
    const [user, setUser] = useState<Admin | null>()
    const [impersonationTarget, setImpersonationTarget] = useState<ImpersonationTarget | null>(null)
    const canImpersonate = useCanImpersonate()

    const navigate = useNavigate()

    const { l10n } = useAppLocalization()

    const { mutateAsync: callDeleteMerchantAdmin } = useApiMutation(deleteAdmin, {
        async onSuccess(_params, data, context) {
            await context.queryClient.invalidateQueries({ queryKey: [MerchantQuery.ADMINS_FOR_MERCHANT] })
            enqueueSnackbar({
                variant: "success",
                message: data?.serviceResponse?.displayMessage,
            })
        },
    })

    function handleDeleteClick() {
        if (user === undefined) {
            return
        } else {
            callDeleteMerchantAdmin({
                userId: user?.user.id ?? 0,
            })
        }
    }

    const merchantAdmins = useApiQuery(fetchMerchantAdmins, {
        queryName: MerchantQuery.ADMINS_FOR_MERCHANT,
        dependencies: {
            merchantId: merchantId ?? 0,
        },
        select: transformData,
    })

    const { ApiQueryTable, columnFactory } = useApiQueryTable(merchantAdmins)

    const columns = useMemo<MRT_ColumnDef<Admin>[]>(
        () => [
            columnFactory.nameColumn({
                header: l10n.getString("office-manager-users-table-name-column"),
                accessorKey: "user.displayName",
                accessorFn: (row) => row.user.displayName,
            }),
            columnFactory.nameColumn({
                header: l10n.getString("office-manager-users-table-email-column"),
                accessorKey: "user.email",
            }),
            columnFactory.activeInactiveStatusColumn(
                { header: l10n.getString("office-manager-users-table-status-column"), accessorKey: "user.isActive" },

                (row) => row.user.isActive,
            ),
        ],
        [columnFactory, l10n],
    )

    return (
        <>
            <LocalizedStrict id="merchant-admin-users-page-title" attrs={{ title: true }}>
                <PageHeader
                    title="Users"
                    rightElement={
                        <LocalizedStrict id="merchant-admin-users-create-admin-button">
                            <StandardButton
                                onClick={() => {
                                    navigate(MERCHANT_ADMIN_CREATE_ROUTE)
                                }}
                            >
                                Create admin
                            </StandardButton>
                        </LocalizedStrict>
                    }
                    page={Page.MERCHANT_USERS}
                    icon={<FontAwesomeIcon icon={faInfoCircle} />}
                >
                    <StandardBreadcrumbs>
                        <LocalizedStrict id="merchant-nav-item-home">
                            <BreadcrumbLink to={MERCHANT_ADMIN_ROUTE}>Home</BreadcrumbLink>
                        </LocalizedStrict>

                        <LocalizedStrict id="merchant-nav-item-account">
                            <Breadcrumb>Account</Breadcrumb>
                        </LocalizedStrict>

                        <LocalizedStrict id="merchant-nav-item-users">
                            <Breadcrumb>Administrators</Breadcrumb>
                        </LocalizedStrict>
                    </StandardBreadcrumbs>
                </PageHeader>
            </LocalizedStrict>

            <ApiQueryTable
                columns={columns}
                renderRowActionMenuItems={({ row, closeMenu }) => [
                    <LocalizedStrict id="office-manager-user-show-details">
                        <MenuItem
                            key={row.original.user.id}
                            onClick={async () => {
                                setUser(row.original)
                                setShowDetailsModal(true)
                                closeMenu()
                            }}
                        >
                            Show details
                        </MenuItem>
                    </LocalizedStrict>,
                    <LocalizedStrict id="office-manager-user-edit">
                        <MenuItem
                            key={row.original.user.id}
                            onClick={() => {
                                navigate(getEditMerchantAdminRoute(row.original.user.id))
                                closeMenu()
                            }}
                        >
                            Edit user
                        </MenuItem>
                    </LocalizedStrict>,
                    canImpersonate && (
                        <LocalizedStrict
                            key={`impersonate-${row.original.user.id}`}
                            id="merchant-admin-users-table-button-impersonate"
                        >
                            <MenuItem
                                onClick={() => {
                                    setImpersonationTarget(toImpersonationTarget(row.original.user))
                                    closeMenu()
                                }}
                            >
                                Log in as
                            </MenuItem>
                        </LocalizedStrict>
                    ),
                    <LocalizedStrict id="office-manager-user-delete">
                        <DestructiveMenuItem
                            key={row.original.user.id}
                            onClick={() => {
                                setUser(row.original)
                                setShowDeleteModal(true)
                                closeMenu()
                            }}
                        >
                            Delete user
                        </DestructiveMenuItem>
                    </LocalizedStrict>,
                ]}
            />

            {user && (
                <DeleteConfirmDialog
                    typeOfEntity={l10n.getString("office-manager-user-delete-title")}
                    entityName={user.user.displayName}
                    handleClick={handleDeleteClick}
                    open={showDeleteModal}
                    onClose={() => setShowDeleteModal(false)}
                />
            )}

            {user && (
                <UserDetailsDialog
                    userId={user.user.id}
                    open={showDetailsModal}
                    userType="admin"
                    onClose={() => setShowDetailsModal(false)}
                />
            )}

            <ImpersonationConfirmDialog target={impersonationTarget} onClose={() => setImpersonationTarget(null)} />
        </>
    )
}
